import React, { useState, useMemo } from "react";
import { Button, Box } from "@mui/material";
import "./mobileSubFooter.css";
import { useNitroStore } from "../../../../store/Store";
import { icons } from "../../../../svg";
import ProductMobileFilter from "../../../../pages/home/components/filters/components/productMobilFilter";
import {
  generateSplitAwareMonths,
  findSelectedMonthOption,
} from "../../multiLocQuoteDetailsPanel/multiLocQuoteDetailsUtils";

interface MobileSubFooterProps {
  onClose?: () => void;
  onCloseAll?: () => void;
  onMapClick?: () => void;
  forceMapButton?: boolean;
  selectedMonth?: string;
  setSelectedMonth?: (value: string) => void;
  onBeforeProductChange?: () => Promise<void>;
  onBeforeListClick?: () => Promise<void>;
  onProductSelectClick?: () => void;
  onMonthSelectClick?: () => void;
  splitMonthKeys?: string[];
}

const MobileSubFooter: React.FC<MobileSubFooterProps> = ({
  onClose,
  onCloseAll,
  onMapClick,
  forceMapButton,
  selectedMonth,
  setSelectedMonth,
  onBeforeProductChange,
  onBeforeListClick,
  onProductSelectClick,
  onMonthSelectClick,
  splitMonthKeys,
}) => {
  const [
    priorityProducts,
    selectedProduct,
    setProduct,
    showMobileFavoriteOriginPanel,
    setShowMobileFavoriteOriginPanel,
  ] = useNitroStore((state) => [
    state.priorityProducts,
    state.product,
    state.setProduct,
    state.showMobileFavoriteOriginPanel,
    state.setShowMobileFavoriteOriginPanel,
  ]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isMonthFilterOpen, setIsMonthFilterOpen] = useState(false);
  const shouldShowMapButton = forceMapButton ?? showMobileFavoriteOriginPanel;

  const splitMonthKeySet = useMemo(
    () => new Set(splitMonthKeys || []),
    [splitMonthKeys],
  );

  const months = useMemo(
    () => generateSplitAwareMonths(Array.from(splitMonthKeySet)),
    [splitMonthKeySet],
  );

  const selectedMonthOption = useMemo(
    () => findSelectedMonthOption(months, selectedMonth),
    [months, selectedMonth],
  );

  const handleListClick = () => {
    if (onBeforeListClick) {
      onBeforeListClick().finally(() => setShowMobileFavoriteOriginPanel(true));
    } else {
      setShowMobileFavoriteOriginPanel(true);
    }
  };

  const handleMapClick = () => {
    if (onMapClick) {
      onMapClick();
    }
    if (onCloseAll) {
      onCloseAll();
    } else if (onClose) {
      onClose();
    } else {
      setShowMobileFavoriteOriginPanel(false);
    }
  };

  const formatProductName = (name: string) => {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  const renderProductButton = () => (
    <Button
      id="mobile-sub-footer-product-select"
      className="product-button-multi-loc"
      onClick={() => {
        if (onProductSelectClick) onProductSelectClick();
        setIsFilterOpen(!isFilterOpen);
      }}
      sx={{
        marginLeft: "10px",
        minWidth: "120px",
        maxWidth: "120px",
      }}
    >
      <span className="product-text" id="mobile-product-select-button-text">
        {formatProductName(selectedProduct)}
      </span>
      <img
        id="mobile-product-select-button-icon-sub-footer"
        data-tooltip-id="downward-expand-icon"
        src={icons.DownwardExpandIcon}
        alt="Downward Expand Icon"
        className="expand-icon"
      />
    </Button>
  );

  const renderMonthButton = () => {
    const currentMonth = selectedMonthOption;
    return (
      <Button
        id="mobile-sub-footer-month-select"
        className="product-button-multi-loc"
        onClick={(e) => {
          e.stopPropagation();
          if (setSelectedMonth) {
            if (onMonthSelectClick) onMonthSelectClick();
            setIsMonthFilterOpen(!isMonthFilterOpen);
          }
        }}
        disabled={!setSelectedMonth}
        sx={{
          opacity: !setSelectedMonth ? 0.5 : 1,
          minWidth: "80px",
          maxWidth: "100px",
          whiteSpace: "nowrap",
          cursor: setSelectedMonth ? "pointer" : "not-allowed",
        }}
      >
        <span className="product-text" id="mobile-sub-footer-month-select-button-text">
          {currentMonth?.display || months[0]?.display || "Month"}
        </span>
        <img
          id="mobile-month-select-button-icon-sub-footer"
          data-tooltip-id="downward-expand-icon"
          src={icons.DownwardExpandIcon}
          alt="Downward Expand Icon"
          className="expand-icon"
        />
      </Button>
    );
  };

  return (
    <div className="footer-container" id="mobile-sub-footer-container">
      {isFilterOpen && (
        <>
          <div
            className="filter-overlay full-width-sheet"
            id="mobile-filter-overlay-wrapper"
          >
            <ProductMobileFilter
              selectedProduct={selectedProduct}
              setSelectedProduct={(val) => {
                if (onBeforeProductChange) {
                  onBeforeProductChange().finally(() => {
                    setProduct(val);
                    setTimeout(() => setIsFilterOpen(false), 50);
                  });
                } else {
                  setProduct(val);
                  setTimeout(() => setIsFilterOpen(false), 50);
                }
              }}
              products={priorityProducts}
              onClose={() => setIsFilterOpen(false)}
              isColumnLayout={false}
            />
          </div>

          <div
            id="mobile-filter-backdrop"
            className="backdrop"
            role="button"
            tabIndex={-1}
            aria-label="Close menu"
            onClick={(e) => {
              e.stopPropagation();
              setIsFilterOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setIsFilterOpen(false);
              }
            }}
          />
        </>
      )}

      {isMonthFilterOpen && setSelectedMonth && (
        <>
          <div
            className="filter-overlay full-width-sheet"
            id="mobile-month-filter-overlay-wrapper"
          >
            <div className="month-filter-content">
              <div className="month-filter-header">
                <span className="month-filter-title">Select Month</span>
                <button
                  type="button"
                  className="month-filter-close"
                  onClick={() => setIsMonthFilterOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="month-filter-list">
                {months.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    className={`month-filter-item ${
                      m.value === selectedMonthOption?.value ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedMonth(m.value);
                      setTimeout(() => setIsMonthFilterOpen(false), 50);
                    }}
                  >
                    {m.display}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div
            id="mobile-month-filter-backdrop"
            className="backdrop"
            role="button"
            tabIndex={-1}
            aria-label="Close menu"
            onClick={(e) => {
              e.stopPropagation();
              setIsMonthFilterOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setIsMonthFilterOpen(false);
              }
            }}
          />
        </>
      )}

      <div className="footer" id="mobile-sub-footer-inner">
        {shouldShowMapButton ? (
          <Box
            id="mobile-footer-map-controls-container"
            className="footer-controls-box"
          >
            <Button
              id="mobile-map-button"
              onClick={handleMapClick}
              className="footer-icon-button"
            >
              <img
                id="mobile-map-button-icon"
                data-tooltip-id="map-icon"
                src={icons.MapButtonIcon}
                alt="Map Icon"
              />
            </Button>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {renderMonthButton()}
              {renderProductButton()}
            </Box>
          </Box>
        ) : (
          <Box
            id="mobile-sub-footer-landing-panel"
            className="footer-controls-box"
          >
            <Button
              id="sub-footer-list-button"
              onClick={handleListClick}
              className="footer-icon-button"
            >
              <img
                id="sub-footer-list-icon"
                src={icons.ListIcon}
                alt="ListAction"
              />
            </Button>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {renderMonthButton()}
              {renderProductButton()}
            </Box>
          </Box>
        )}
      </div>
    </div>
  );
};

export default MobileSubFooter;
